import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { User, auth } from '@/lib/supabase'
import { toast } from 'sonner'

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<{ success: boolean; requireCaptcha?: boolean }>
  logout: () => Promise<void>
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Check for existing session
    try {
      const currentUser = auth.getCurrentUser()
      setUser(currentUser)
    } catch (error) {
      // Error getting current user - handled by setting user to null
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  const login = async (email: string, password: string) => {
    try {
      setLoading(true)
      const { user: loggedInUser, requireCaptcha } = await auth.login(email, password)
      
      setUser(loggedInUser)
      auth.setCurrentUser(loggedInUser)
      
      toast.success(`Welcome back, ${loggedInUser.role}!`)
      return { success: true, requireCaptcha }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Login failed')
      return { success: false }
    } finally {
      setLoading(false)
    }
  }

  const logout = async () => {
    try {
      await auth.logout()
      setUser(null)
      toast.success('Logged out successfully')
    } catch (error) {
      toast.error('Logout failed')
    }
  }

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}